import React, { useState } from "react";
import { motion } from "framer-motion";
import { useUser } from "../../contextapi/UserContext";
import { useUserPermissions } from "../../hooks/useUserPermissions";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  User,
  Shield,
  Building,
  Users,
  Eye,
  EyeOff,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Crown,
  Settings,
  Loader2,
} from "lucide-react";

export function UserPermissionsDiagnostic() {
  const { user } = useUser();
  const { permissions, roleData, loading, error, refreshPermissions } = useUserPermissions();
  const [showDetails, setShowDetails] = useState(false);

  if (!user) {
    return (
      <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-red-800 dark:text-red-200">
            <XCircle className="w-5 h-5" />
            <span>No User Found</span>
          </CardTitle>
          <CardDescription className="text-red-700 dark:text-red-300">
            User is not authenticated or loaded.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const getRoleDisplayName = (role: string | null) => {
    if (!role) return "None";
    
    const roleMap: Record<string, string> = {
      'guest': 'Guest',
      'user': 'User',
      'org_admin': 'Organization Admin',
      'org_member': 'Organization Member',
      'group_admin': 'Team Admin',
      'group_member': 'Team Member',
      'platform_admin': 'Platform Admin',
      'super': 'Super Admin',
    };
    
    return roleMap[role] || role;
  };

  const getPermissionIcon = (hasPermission: boolean) => {
    return hasPermission ? (
      <CheckCircle className="w-4 h-4 text-green-600" />
    ) : (
      <XCircle className="w-4 h-4 text-red-600" />
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* User Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <User className="w-5 h-5" />
              <span>User Information</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshPermissions}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">User ID</label>
              <p className="font-mono text-sm bg-muted p-2 rounded">{user.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Email</label>
              <p className="text-sm">{user.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20">
          <CardContent className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span>Loading permissions...</span>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-800 dark:text-red-200">
              <AlertCircle className="w-5 h-5" />
              <span>Permission Error</span>
            </CardTitle>
            <CardDescription className="text-red-700 dark:text-red-300">
              {error}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Role Data */}
      {roleData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="w-5 h-5" />
              <span>Current Roles</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Profile Role</label>
                <div className="flex items-center space-x-2 mt-1">
                  <Badge variant={roleData.profileRole === 'guest' ? 'destructive' : 'default'}>
                    {getRoleDisplayName(roleData.profileRole)}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Organization Role</label>
                <div className="flex items-center space-x-2 mt-1">
                  <Badge variant={roleData.organizationRole === 'org_admin' ? 'default' : 'secondary'}>
                    {roleData.organizationRole === 'org_admin' && <Crown className="w-3 h-3 mr-1" />}
                    {getRoleDisplayName(roleData.organizationRole)}
                  </Badge>
                </div>
              </div>
            </div>

            {roleData.platformAdminLevel && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Platform Admin Level</label>
                <div className="flex items-center space-x-2 mt-1">
                  <Badge variant="destructive">
                    <Crown className="w-3 h-3 mr-1" />
                    {getRoleDisplayName(roleData.platformAdminLevel)}
                  </Badge>
                </div>
              </div>
            )}

            {roleData.teamRoles.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Team Roles</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {roleData.teamRoles.map((teamRole) => (
                    <Badge key={teamRole.teamId} variant="outline">
                      <Users className="w-3 h-3 mr-1" />
                      {teamRole.teamName}: {getRoleDisplayName(teamRole.role)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Permissions Summary */}
      {permissions && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Settings className="w-5 h-5" />
                <span>Permissions Summary</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showDetails ? 'Hide' : 'Show'} Details
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Global Permissions */}
            <div>
              <h4 className="font-medium mb-2">Global Status</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(!permissions.isGuest)}
                  <span className="text-sm">Not Guest</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(permissions.isOrgAdmin)}
                  <span className="text-sm">Org Admin</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(permissions.isOrgMember)}
                  <span className="text-sm">Org Member</span>
                </div>
                <div className="flex items-center space-x-2">
                  {getPermissionIcon(permissions.isPlatformAdmin)}
                  <span className="text-sm">Platform Admin</span>
                </div>
              </div>
            </div>

            {/* Feature Permissions */}
            {showDetails && (
              <div>
                <h4 className="font-medium mb-2">Feature Permissions</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries({
                    'Edit Profile': permissions.canEditProfile,
                    'View Organizations': permissions.canViewOrganizations,
                    'Edit Organizations': permissions.canEditOrganizations,
                    'Manage Teams': permissions.canManageTeams,
                    'Create Teams': permissions.canCreateTeams,
                    'Edit Branding': permissions.canEditBranding,
                    'View Security': permissions.canViewSecurity,
                    'Edit Security': permissions.canEditSecurity,
                    'View Subscription': permissions.canViewSubscription,
                    'Edit Subscription': permissions.canEditSubscription,
                    'Edit Preferences': permissions.canEditPreferences,
                  }).map(([feature, hasPermission]) => (
                    <div key={feature} className="flex items-center space-x-2">
                      {getPermissionIcon(hasPermission)}
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Team Permissions */}
            {showDetails && Object.keys(permissions.teamPermissions).length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Team Permissions</h4>
                <div className="space-y-2">
                  {Object.entries(permissions.teamPermissions).map(([teamId, teamPerms]) => {
                    const teamRole = roleData?.teamRoles.find(t => t.teamId === teamId);
                    return (
                      <div key={teamId} className="border rounded p-3">
                        <h5 className="font-medium text-sm mb-2">
                          {teamRole?.teamName || teamId}
                        </h5>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center space-x-2">
                            {getPermissionIcon(teamPerms.isGroupAdmin)}
                            <span className="text-xs">Group Admin</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getPermissionIcon(teamPerms.isGroupMember)}
                            <span className="text-xs">Group Member</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getPermissionIcon(teamPerms.canManageTeam)}
                            <span className="text-xs">Can Manage</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getPermissionIcon(teamPerms.canViewTeam)}
                            <span className="text-xs">Can View</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
